import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Megaphone, Loader2, Clock, Users } from "lucide-react";

const NotifyFansCard = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (token) refresh(); }, [token]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/artists/me/broadcast-status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally { setLoading(false); }
  };

  const send = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title and message required"); return; }
    if (!window.confirm(`Send a push + in-app notification to all ${status?.follower_count || 0} of your supporters?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/artists/notify-fans`, { title: title.trim(), message: message.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Broadcast sent to ${res.data.sent} fan${res.data.sent === 1 ? "" : "s"}!`);
      setTitle(""); setMessage("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send broadcast");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="card p-6"><div className="h-32 animate-pulse bg-[#1A2030] rounded" /></div>;
  if (!status?.has_artist_profile) return null;

  const cooldownActive = !!status.next_allowed_at;

  return (
    <div className="card p-6 relative overflow-hidden" data-testid="notify-fans-card">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#EC008C] via-[#FF3399] to-[#FF6BC5]" />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-[#EC008C]" />
            </div>
            <div>
              <h3 className="font-['Unbounded'] text-xl font-bold text-white">Notify All My Fans</h3>
              <p className="text-xs text-[#8A92A0]">One push + in-app notification per week.</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#8A92A0] uppercase tracking-wide flex items-center gap-1 justify-end"><Users className="w-3 h-3" /> Reach</p>
            <p className="text-2xl font-bold text-[#EC008C]" data-testid="follower-count">{status.follower_count}</p>
          </div>
        </div>

        {cooldownActive ? (
          <div className="bg-[#1A2030]/40 rounded-xl p-5 border border-[#1A2030] text-center" data-testid="cooldown-state">
            <Clock className="w-8 h-8 text-[#8A92A0] mx-auto mb-2" />
            <p className="text-sm text-white">You've used this week's broadcast.</p>
            <p className="text-xs text-[#8A92A0] mt-1">Next available {new Date(status.next_allowed_at).toLocaleString()}.</p>
          </div>
        ) : status.follower_count === 0 ? (
          <div className="bg-[#1A2030]/40 rounded-xl p-5 border border-[#1A2030] text-center" data-testid="no-fans-state">
            <Users className="w-8 h-8 text-[#8A92A0] mx-auto mb-2" />
            <p className="text-sm text-white">No followers yet.</p>
            <p className="text-xs text-[#8A92A0] mt-1">Once fans follow you, you can broadcast to all of them at once.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <input value={title} onChange={e => setTitle(e.target.value.slice(0,80))} placeholder="Headline (e.g. New track tonight)"
              className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white" data-testid="broadcast-title" />
            <textarea value={message} onChange={e => setMessage(e.target.value.slice(0,280))} rows={3} placeholder="Tell them what's happening..."
              className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white resize-none" data-testid="broadcast-message" />
            <p className="text-xs text-[#4A5260] text-right">{message.length}/280</p>
            <button onClick={send} disabled={busy} className="w-full px-4 py-2.5 rounded-lg bg-[#EC008C] text-[#000000] font-semibold hover:bg-[#FF3399] disabled:opacity-60 flex items-center justify-center gap-2 text-sm" data-testid="broadcast-send-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              {busy ? "Broadcasting..." : `Broadcast to ${status.follower_count} fan${status.follower_count === 1 ? "" : "s"}`}
            </button>
          </div>
        )}

        {status.history?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#1A2030]">
            <p className="text-xs text-[#8A92A0] uppercase tracking-wide mb-2">Recent broadcasts</p>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {status.history.slice(0,5).map(h => (
                <div key={h.broadcast_id} className="bg-[#050810] rounded-lg p-2.5 border border-[#1A2030]" data-testid="broadcast-history-entry">
                  <p className="text-sm text-white truncate">{h.title}</p>
                  <p className="text-xs text-[#8A92A0]">Sent to {h.sent_count} · {new Date(h.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotifyFansCard;
